import React, { useCallback, useEffect, useMemo, useState } from 'react'
import styled, { css } from 'styled-components'
import { theme } from 'styled-tools'
import CheckmarkSVG from '../../checkbox/CheckmarkSVG'
import { Slider } from '../../slider/Slider'

const QuestionHeadingSlider = styled.div`
  font-family: ${theme(`typography.paragraph.fontFamily`)};
  font-size: 18px;
  font-weight: 700;
`

const HeadingAndPreambleContainerSlider = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: end;
`

const QuestionPreamble = styled.div`
  display: flex;
  flex-direction: column;
  font-family: ${theme('typography.paragraph.fontFamily')};
  font-size: ${theme('quickQuestions.preamble.fontSize.mobile')};
  font-weight: ${(props) =>
    theme('quickQuestions.preamble.fontWeight')(props) || '300'};
  line-height: ${theme('quickQuestions.preamble.lineHeight')};
  margin: 8px 0px 8px 0px;
  color: ${theme(`quickQuestions.preamble.color`)};
  margin-top: 0;

  a {
    color: ${theme(`colors.link`)};
  }

  p {
    padding-top: 0;
    margin-top: 0;
    font-size: ${theme('quickQuestions.preamble.fontSize.mobile')};
  }

  ul {
    padding: 0;
    margin: 0;
    margin-bottom: 14px;
    margin-top: 0;
  }
  ul > li {
    font-weight: 700;
    list-style-type: disc;
    list-style-position: inside;
    font-size: ${theme('quickQuestions.preamble.fontSize.mobile')};
  }

  strong {
    font-weight: 700;
  }

  ${(props) => props.theme.media.min.sm`
    padding-right: 30px;
    font-size: ${theme('quickQuestions.preamble.fontSize.desktop')};

    p {
      font-size: ${theme('quickQuestions.preamble.fontSize.desktop')};
    }

    ul > li {
      font-size: ${theme('quickQuestions.preamble.fontSize.desktop')};
    }
  `};
`

const AnotherOption = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  font-weight: 400;
  width: 100%;
  margin: ${theme('questionBlock.options.margin.mobile')};
  ${(props) => props.theme.media.min.sm`
    margin:0;
    padding: ${theme('questionBlock.anotherOption.padding', '14px')(props)};
    box-sizing: border-box;
  `}
  ${(props) => props.theme.media.min.lg`
    width: auto;
    min-width: ${theme('questionBlock.anotherOption.minWidth', '160px')(props)};
    justify-content: flex-start;
    justify-content: ${theme('questionBlock.anotherOption.justifyContent')};
  `}
`

const AnotherOptionText = styled.p`
  cursor: pointer;
  color: ${theme('colors.darkSlate')};
  font-weight: ${(props) =>
    theme('questionBlock.anotherOption.text.fontSize')(props) || '700'};
  font-size: ${theme('questionBlock.anotherOption.text.fontSize')};
  text-align: center;
  display: block;
  margin: 0px auto;
  font-family: ${theme('typography.paragraph.fontFamily')};
  ${(props) => props.theme.media.min.sm`
      margin: 0px;
      text-align: right;
      display:inline;
  `}

  ${(props) =>
    props.active &&
    `
    color: ${props.theme.colors.link};
  `}
`

const StyledTextareaWrapper = styled.div`
  ${(props) => props.theme.media.min.sm`
    display: ${theme(
      'questionBlock.textareaWrapper.displayBlock',
      'flex'
    )(props)};
    align-items: flex-end;
  `};
  margin-bottom: 14px;

  & > div {
    margin-bottom: 0;
  }
  ${(props) =>
    props.theme.questionBlock?.textareaWrapper?.flexBasis
      ? css`
          flex-basis: ${theme('questionBlock.textareaWrapper.flexBasis')};
        `
      : ''};
`

const OptionsContainer = styled.div`
  display: ${theme('options.container.display')};
  flex-direction: ${theme('options.container.flexDirection')};
  justify-content: ${theme('options.container.justifyContent')};
  flex-basis: ${(props) =>
    theme('questionBlock.options.container.flexBasis')(props) || '70%'};
  ${(props) =>
    props.active &&
    `
    color: ${props.theme.colors.link};
  `}
  ${(props) =>
    props.hasTextBoxedOption &&
    `
    display: flex;
    align-items: center;
  `}
`

const StyledSliderAreaWrapper = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
`
const StyledSliderOptionsWrapper = styled.div`
  display: flex;
  width: 100%;
  flex-direction: row;
  justify-content: space-between;
`

const StyledSliderCheckbox = styled.span`
  left: 0;
  display: inline-block;
  width: 19px;
  height: 19px;
  margin-right: 8px;
  margin-bottom: 3px;
  border: 1px solid #7f8487;
  border-radius: 3px;
  background-color: #fff;
  transition: background-color 0.1s ease-in;
  vertical-align: middle;
  position: relative;
  ${(props) =>
    props.active &&
    `
    background-color: #000000;
  `}
  svg {
    position: absolute;
    top: 3px;
    left: 3px;
  }
`

export const SliderQuestion = ({ question, answers, onChange }) => {
  const [value, setValue] = useState()

  const options = useMemo(() => question.options[0], [question.options])
  const questionStyle = (question.questionStyle || 'style-1').replace('-', '')

  const isSliderValue = useMemo(
    () => answers[0] !== question.otherAnswerText,
    [answers, question.otherAnswerText]
  )

  useEffect(() => {
    const defaultNumber = parseInt(answers[0]?.replace(/^\D+/g, ''), 10)
    if (isNaN(defaultNumber)) {
      setValue(options.minValue)
    } else {
      setValue(defaultNumber)
    }
  }, [answers])

  const parseValue = useCallback(
    (v) => {
      const newValue = `${options.prefix} ${v}`
      if (v === options.maxValue && options.endSuffix) {
        return `${newValue}${options.endSuffix}`
      }
      return newValue
    },
    [options]
  )

  const onAfterChange = useCallback(
    (v) => {
      const newValue = parseValue(v)
      onChange([newValue])
    },
    [parseValue, onChange]
  )

  const parsedValue = useMemo(() => parseValue(value), [value, parseValue])

  return (
    <OptionsContainer>
      <StyledSliderAreaWrapper>
        <StyledSliderOptionsWrapper>
          <span>{`${options.prefix} ${options.minValue}`}</span>
          <span>
            {`${options.prefix} ${options.maxValue}${options.endSuffix}`}
          </span>
        </StyledSliderOptionsWrapper>
        <StyledTextareaWrapper>
          <Slider
            minValue={options.minValue}
            maxValue={options.maxValue}
            value={value}
            amountInterval={options.amountInterval}
            onChange={setValue}
            onAfterChange={onAfterChange}
          />
        </StyledTextareaWrapper>
        <HeadingAndPreambleContainerSlider>
          {isSliderValue ? (
            <div style={{ marginTop: '16px' }}>
              <QuestionPreamble
                questionStyle={questionStyle}
                dangerouslySetInnerHTML={{
                  __html: question.questionSupportingText,
                }}
              />
              <QuestionHeadingSlider
                questionStyle={questionStyle}
                dangerouslySetInnerHTML={{ __html: parsedValue }}
              />
            </div>
          ) : (
            <QuestionHeadingSlider
              questionStyle={questionStyle}
              style={{ marginTop: '16px' }}
              dangerouslySetInnerHTML={{
                __html: options.placeholderText,
              }}
            />
          )}
          {question.otherAnswerText && (
            <AnotherOption
              data-track={`not-sure-${question.sortOrder}`}
              style={{
                marginTop: '16px',
                marginBottom: '3px',
                justifyContent: 'flex-end',
                width: 'auto',
              }}
            >
              <AnotherOptionText
                active={!isSliderValue}
                onClick={() => {
                  const newValue = isSliderValue
                    ? [question.otherAnswerText]
                    : []
                  setValue(options.minValue)
                  onChange(newValue)
                }}
              >
                <StyledSliderCheckbox active={!isSliderValue}>
                  <CheckmarkSVG />
                </StyledSliderCheckbox>
                {question.otherAnswerText}
              </AnotherOptionText>
            </AnotherOption>
          )}
        </HeadingAndPreambleContainerSlider>
      </StyledSliderAreaWrapper>
    </OptionsContainer>
  )
}
